import { useMount } from 'ahooks';
import { PageHeader, TableColumnsType, Tag, Image } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxTable } from 'components';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxSpin } from 'components/RevxSpin';
import { TreeShaped } from 'domains/appDomain';
import {
  BUSINESS_TYPE_COLLECTION,
  BUSINESS_TYPE_ENUM,
  EMPTY_GALLERY_LABEL,
  SkuGalleryLabel,
} from 'domains/gallerySkuDomain';
import { isEmpty } from 'lodash-es';
import { Key, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UPLOAD_FILE_ADDRESS_PREFIX } from 'utils/constants';
import GalleryLabelItemForm from './GalleryLabelItemForm';

const GalleryLabelList = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { data, item } = useAppSelector((state) => state.galleryLabel);

  const loading = useAppSelector(
    (state) => state.loading.effects.galleryLabel.getList
  );

  useMount(() => dispatch.galleryLabel.getList());

  let treeData: TreeShaped<SkuGalleryLabel>[] = useMemo(() => {
    return (
      data?.galleryLabels
        .map((labelWithCount) => labelWithCount.skuLabel)
        .filter((label) => !label.parentId)
        .map((label) => ({
          ...label,
          children: isEmpty(label.dropDownLabels)
            ? undefined
            : label.dropDownLabels,
        })) || []
    );
  }, [data]);

  const columns: TableColumnsType<TreeShaped<SkuGalleryLabel>> = [
    {
      title: undefined,
      dataIndex: 'id',
      align: 'right',
      width: 40,
      render: () => {
        return '';
      },
    },
    {
      title: t('common.id'),
      dataIndex: 'id',
      width: 60,
    },
    {
      title: t('common.name'),
      dataIndex: 'name',
    },
    {
      title: t('common.description'),
      dataIndex: 'description',
    },
    {
      title: t('platform.resources.galleryLabels.summary'),
      dataIndex: 'summary',
    },
    {
      title: t('platform.resources.galleryLabels.image'),
      dataIndex: 'image',
      render: (value) =>
        isEmpty(value) ? (
          <></>
        ) : (
          <Image width={24} src={UPLOAD_FILE_ADDRESS_PREFIX + value} />
        ),
    },
    {
      title: t('platform.resources.galleryLabels.businessTypes'),
      dataIndex: 'businessTypes',
      render: (value: BUSINESS_TYPE_ENUM[]) =>
        value
          .map((t) => BUSINESS_TYPE_COLLECTION.find((b) => b.id === t))
          .map((v) => (v === undefined ? '' : v.name))
          .join(', '),
    },
    {
      title: t('common.active'),
      dataIndex: 'active',
      render: (active) => (
        <Tag color={active ? 'green' : 'volcano'}>
          {active ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 180,
      render: (value, record) => (
        <RevxFlexLayout>
          <RevxButton
            type="link"
            key="view"
            size="small"
            onClick={() => dispatch.galleryLabel.setState({ item: record })}
          >
            {t('common.view')}
          </RevxButton>
          {!record.parentId && (
            <RevxButton
              type="link"
              key="sub"
              size="small"
              onClick={() =>
                dispatch.galleryLabel.setState({
                  item: {
                    ...EMPTY_GALLERY_LABEL(false),
                    parentId: record.id,
                  },
                })
              }
            >
              {t('platform.resources.galleryLabels.createDropDownLabel')}
            </RevxButton>
          )}
        </RevxFlexLayout>
      ),
    },
  ];

  const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([]);

  const pageHeaderExtra = [
    <RevxButton
      key="create"
      brandGreen
      disabled={!!item}
      onClick={() => {
        dispatch.galleryLabel.setState({
          item: {
            ...EMPTY_GALLERY_LABEL(false),
          },
        });
      }}
    >
      {t('platform.resources.galleryLabels.create')}
    </RevxButton>,
  ];

  return (
    <RevxSpin spinning={loading}>
      <PageHeader
        title={t('platform.resources.tabs.galleryLabels')}
        className="platform-page-header"
        ghost={false}
        extra={pageHeaderExtra}
      />
      <div hidden={!!item}>
        <RevxTable<TreeShaped<SkuGalleryLabel>>
          size="small"
          loading={loading}
          columns={columns}
          dataSource={treeData}
          pagination={false}
          expandable={{
            expandRowByClick: true,
            onExpandedRowsChange: (rows) => {
              setExpandedRowKeys([...rows]);
            },
          }}
          rowClassName={(record: TreeShaped<SkuGalleryLabel>) => {
            let cls = '';
            if (record.children) {
              cls += ' expandable';
            }
            if (expandedRowKeys.includes(record.id) && record.children) {
              cls += ' expanded';
            } else if (record.parentId) {
              cls += ' nested';
            }
            return cls;
          }}
        />
      </div>
      {item && <GalleryLabelItemForm isCompany={false} />}
    </RevxSpin>
  );
};

export default GalleryLabelList;
